import {
  Autocomplete,
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import InventoryDTO from '@src/adapters/dto/inventory/InventoryDTO';
import useSaveInventoryLazy from '@src/adapters/hooks/inventory/useSaveInventory';
import CategoryAutoComplete from '@src/components/auto-complete/CategoryAutoComplete';
import InventoryDelete from './InventoryDelete';

interface Props {
  inventory: InventoryDTO;
  cancel(): void;
  saved(inventory: InventoryDTO): void;
  deleted(id: string): void;
}

function InventoryDetails({ inventory, cancel, saved, deleted }: Props) {
  const { execute: save } = useSaveInventoryLazy();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedAliases, setSelectedAliases] = useState<string[]>([]);
  const [inventoryAliases, setInventoryAliases] = useState<string[]>([]);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { handleSubmit, control, formState, reset, setValue } =
    useForm<InventoryDTO>({
      mode: 'all',
    });

  useEffect(() => {
    setValue('aliases', [...new Set(inventory.aliases)]);
  }, [inventory, setValue]);

  useEffect(() => {
    if (inventory && inventory.aliases && inventory.aliases.length > 0) {
      const newAliases = [...new Set(inventory.aliases)];
      setInventoryAliases(newAliases);

      setSelectedAliases([]);
      setValue('aliases', []);

      setTimeout(() => {
        setSelectedAliases(newAliases);
        setValue('aliases', newAliases);
      }, 50);
    } else {
      setInventoryAliases([]);
      setTimeout(() => {
        setSelectedAliases([]);
        setValue('aliases', []);
      }, 50);
    }
  }, [inventory, setValue]);

  useEffect(() => {
    reset(inventory);
  }, [inventory, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      let inv: InventoryDTO = {
        id: data.id,
        name: data.name,
        categoryId: data.categoryId,
        manufacturerId: data.manufacturerId,
        aliases: data.aliases,
      };
      const result = await save({ inventory: inv });
      saved(result.inventory);
    } catch (err: any) {
      enqueueSnackbar(err.message ? err.message : err, { variant: 'error' });
    }
  });

  const handleDelete = () => {
    setDeleteOpen(true);
  };

  const handleDeleteClose = (isDeleted: boolean) => {
    setDeleteOpen(false);
    if (isDeleted) {
      deleted(inventory.id!);
    }
  };

  return (
    <Container>
      {inventory && (
        <>
          <Box>
            <Typography variant="h5" sx={{ paddingBottom: '20px' }}>
              Details
            </Typography>
            <form onSubmit={onSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Controller
                        name="name"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <TextField
                            fullWidth
                            {...field}
                            label="Name"
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CategoryAutoComplete
                        control={control}
                        name="categoryId"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Controller
                        name="aliases"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            options={inventoryAliases}
                            value={selectedAliases}
                            onChange={(_, newValue) => {
                              setSelectedAliases(newValue);
                              field.onChange(newValue);
                            }}
                            freeSolo={false}
                            multiple
                            renderInput={(params) => (
                              <TextField {...params} label="Aliases" />
                            )}
                            isOptionEqualToValue={(option, value) =>
                              option === value
                            }
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    color="error"
                    fullWidth
                    onClick={handleDelete}
                    variant="contained">
                    delete
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    color="secondary"
                    fullWidth
                    variant="outlined"
                    onClick={cancel}>
                    cancel
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    color="primary"
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={!formState.isValid}>
                    save
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
          <InventoryDelete
            id={inventory.id!}
            open={deleteOpen}
            close={handleDeleteClose}
          />
        </>
      )}
    </Container>
  );
}

export default InventoryDetails;
